/**
 * <k-slider> — the single, dependency-free slider used across the theme.
 *
 * Built on native CSS scroll-snap, so swiping, trackpads and momentum scroll
 * work without JavaScript. This script only adds arrows, pagination,
 * autoplay, looping, keyboard support and theme editor integration.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class KSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(':scope > .slider__track');
    if (!this.track) return;

    this.prevButton = this.querySelector(':scope > .slider__controls [data-prev]');
    this.nextButton = this.querySelector(':scope > .slider__controls [data-next]');
    this.paginationEl = this.querySelector(':scope > .slider__controls [data-pagination-container]');
    this.isFade = this.dataset.effect === 'fade';
    this.loop = this.hasAttribute('data-loop');
    this.autoplayDelay = parseFloat(this.dataset.autoplay || '0') * 1000;
    this.index = 0;
    this.positions = [0];
    this.paused = false;
    this.userInteracted = false;

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);

    this.prevButton?.addEventListener('click', () => this.step(-1, true));
    this.nextButton?.addEventListener('click', () => this.step(1, true));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);
    this.track.addEventListener('pointerdown', () => this.markInteracted(), { passive: true });
    this.track.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.markInteracted();
    }, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.scheduleMeasure());
    this.resizeObserver.observe(this.track);

    this.mutationObserver = new MutationObserver(() => this.scheduleMeasure());
    this.mutationObserver.observe(this.track, { childList: true });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    this.measure();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.stopAutoplay();
    cancelAnimationFrame(this.measureFrame);
    cancelAnimationFrame(this.scrollFrame);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  get slides() {
    return Array.from(this.track.children).filter(
      (el) => el.nodeType === 1 && !el.hidden && el.offsetParent !== null
    );
  }

  /** Index of the snap position that brings the given slide into view. */
  indexOfSlide(slide) {
    if (this.isFade) return Math.max(0, this.slides.indexOf(slide));
    const paddingStart = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
    const target = slide.offsetLeft - paddingStart;
    let best = 0;
    this.positions.forEach((value, i) => {
      if (Math.abs(value - target) < Math.abs(this.positions[best] - target)) best = i;
    });
    return best;
  }

  /** Work out every distinct snap position the track can rest at. */
  scheduleMeasure() {
    if (this.measureFrame) return;
    this.measureFrame = requestAnimationFrame(() => {
      this.measureFrame = null;
      if (this.isConnected) this.measure();
    });
  }

  measure() {
    const slides = this.slides;

    if (this.isFade) {
      this.positions = slides.map((_, i) => i);
      slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.index));
    } else {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const paddingStart = parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0;
      const centered = this.dataset.align === 'center';
      const positions = [];

      slides.forEach((slide) => {
        let left = slide.offsetLeft - paddingStart;
        if (centered) left = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
        const clamped = Math.max(0, Math.min(Math.round(left), Math.max(0, Math.round(maxScroll))));
        if (!positions.length || Math.abs(clamped - positions[positions.length - 1]) > 2) {
          positions.push(clamped);
        }
      });

      this.positions = positions.length ? positions : [0];
    }

    const scrollable = this.positions.length > 1;
    this.toggleAttribute('data-scrollable', scrollable);

    slides.forEach((slide, i) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (!slide.hasAttribute('aria-label')) {
        const template = window.theme?.strings?.slide || '[index] / [count]';
        slide.setAttribute(
          'aria-label',
          template.replace('[index]', i + 1).replace('[count]', slides.length)
        );
      }
    });

    this.buildPagination();
    this.syncIndex();
    this.update();
  }

  buildPagination() {
    if (!this.paginationEl || this.dataset.pagination !== 'dots') return;
    const count = this.positions.length;
    if (this.paginationEl.childElementCount === count) return;

    const fragment = document.createDocumentFragment();
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'slider__dot';
      dot.setAttribute('aria-label', `${i + 1} / ${count}`);
      dot.addEventListener('click', () => this.goTo(i, true));
      fragment.appendChild(dot);
    }
    this.paginationEl.replaceChildren(fragment);
  }

  syncIndex() {
    if (this.isFade) return;
    const scrollLeft = this.track.scrollLeft;
    let closest = 0;
    let distance = Infinity;
    this.positions.forEach((position, i) => {
      const d = Math.abs(position - scrollLeft);
      if (d < distance) {
        distance = d;
        closest = i;
      }
    });
    this.index = closest;
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.syncIndex();
      this.update();
    });
  }

  update() {
    const count = this.positions.length;
    if (this.lastIndex !== this.index) {
      this.lastIndex = this.index;
      this.dispatchEvent(new CustomEvent('slider:change', { detail: { index: this.index }, bubbles: true }));
    }
    const atStart = this.index <= 0;
    const atEnd = this.index >= count - 1;

    if (this.prevButton) this.prevButton.disabled = !this.loop && atStart;
    if (this.nextButton) this.nextButton.disabled = !this.loop && atEnd;

    const type = this.dataset.pagination;
    if (!this.paginationEl || type === 'none') return;

    if (type === 'dots') {
      Array.from(this.paginationEl.children).forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    } else if (type === 'fraction') {
      const fraction = this.paginationEl.querySelector('.slider__fraction');
      if (fraction) fraction.textContent = `${this.index + 1} / ${count}`;
    } else if (type === 'progress') {
      const bar = this.paginationEl.querySelector('.slider__progress-bar');
      if (!bar) return;
      let size;
      let progress;
      if (this.isFade) {
        size = 1 / count;
        progress = count > 1 ? this.index / (count - 1) : 0;
      } else {
        const max = this.track.scrollWidth - this.track.clientWidth;
        size = this.track.clientWidth / this.track.scrollWidth;
        progress = max > 0 ? this.track.scrollLeft / max : 0;
      }
      const sizePercent = Math.min(100, size * 100);
      bar.style.setProperty('--progress-size', `${sizePercent}%`);
      bar.style.setProperty('--progress-offset', `${progress * (100 / (sizePercent / 100) - 100)}%`);
    }
  }

  step(direction, fromUser = false) {
    const count = this.positions.length;
    let next = this.index + direction;
    if (next >= count) next = this.loop || !fromUser ? 0 : count - 1;
    if (next < 0) next = this.loop ? count - 1 : 0;
    this.goTo(next, fromUser);
  }

  goTo(index, fromUser = false) {
    if (fromUser) this.markInteracted();
    const count = this.positions.length;
    this.index = Math.max(0, Math.min(index, count - 1));

    if (this.isFade) {
      this.slides.forEach((slide, i) => slide.toggleAttribute('data-active', i === this.index));
      this.update();
      return;
    }

    this.track.scrollTo({
      left: this.positions[this.index],
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
    this.update();
  }

  onKeydown(event) {
    if (event.target !== this.track) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1, true);
    }
  }

  /* ---------- Autoplay ---------- */
  setupAutoplay() {
    if (!this.autoplayDelay || reducedMotion.matches) return;

    const pause = () => {
      this.paused = true;
    };
    const resume = () => {
      this.paused = false;
    };
    this.addEventListener('mouseenter', pause);
    this.addEventListener('mouseleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.startAutoplay();
      else this.stopAutoplay();
    });
    this.intersectionObserver.observe(this);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.userInteracted) return;
    this.timer = setInterval(() => {
      if (this.paused || document.hidden || this.positions.length < 2) return;
      this.step(1);
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    clearInterval(this.timer);
    this.timer = null;
  }

  markInteracted() {
    // Stop autoplay for good once a shopper takes control of the slider.
    this.userInteracted = true;
    this.stopAutoplay();
  }

  /* ---------- Theme editor ---------- */
  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const slides = this.slides;
    const slideIndex = slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
    if (slideIndex < 0) return;
    this.stopAutoplay();
    this.paused = true;

    if (this.isFade) {
      this.goTo(slideIndex);
      return;
    }
    const target = slides[slideIndex];
    const position = this.positions.reduce(
      (best, value, i) => (Math.abs(value - target.offsetLeft) < Math.abs(this.positions[best] - target.offsetLeft) ? i : best),
      0
    );
    this.goTo(position);
  }

  onBlockDeselect(event) {
    if (!this.contains(event.target)) return;
    this.paused = false;
    if (this.autoplayDelay && this.visible && !this.userInteracted) this.startAutoplay();
  }
}

if (!customElements.get('k-slider')) {
  customElements.define('k-slider', KSlider);
}
